/**
 * Single row inside the Parts Master "Columns" popover.
 *
 * Layout matches the PO Lines column manager (POLinesTable.tsx ~227-238):
 * GripVertical handle, checkbox, label, Eye / EyeOff icon. Drag-and-drop
 * uses native HTML5 events (`onDragStart` / `onDragOver` / `onDrop`) so
 * there's no third-party DnD dependency.
 *
 * Sticky-left columns (Part Number, Description) render with the checkbox
 * disabled and no drag handle — their physical position is hard-coded into
 * the table styling so they cannot be hidden or reordered.
 */
import { useState, type DragEvent } from 'react';
import { Eye, EyeOff, GripVertical } from 'lucide-react';
import type { PartsMasterColumnDescriptor } from './usePartsMasterVisibleColumns';

interface PartsMasterColumnRowProps {
  column: PartsMasterColumnDescriptor;
  visible: boolean;
  /** False = sticky-left, no drag handle, checkbox disabled. */
  draggable: boolean;
  /**
   * Position within the *draggable slice* (0-based). Used as the payload for
   * native HTML5 drag events. Ignored when `draggable` is false.
   */
  index: number;
  onToggle: (id: string) => void;
  /** Move handler — invoked when a drop reorders this row. */
  onReorder: (dragIndex: number, hoverIndex: number) => void;
}

/**
 * Render one row in the column manager.
 *
 * Pass `draggable={false}` for sticky-left rows so they neither show a drag
 * handle nor participate in the HTML5 drag interaction.
 */
export default function PartsMasterColumnRow({
  column,
  visible,
  draggable,
  index,
  onToggle,
  onReorder,
}: PartsMasterColumnRowProps) {
  if (!draggable) {
    return (
      <div
        className="flex items-center gap-2 px-2 py-1.5 rounded opacity-70 cursor-default"
        title="Sticky column — always visible"
      >
        {/* Spacer to keep the checkbox column aligned with draggable rows. */}
        <span className="inline-block w-4 h-4" aria-hidden="true" />
        <input
          type="checkbox"
          checked={visible}
          disabled
          readOnly
          className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary cursor-not-allowed"
        />
        <span className="text-sm flex-1">{column.label}</span>
        {visible ? (
          <Eye className="h-3 w-3 text-muted-foreground" />
        ) : (
          <EyeOff className="h-3 w-3 text-muted-foreground" />
        )}
      </div>
    );
  }

  return (
    <DraggableRow
      column={column}
      visible={visible}
      index={index}
      onToggle={onToggle}
      onReorder={onReorder}
    />
  );
}

interface DraggableRowProps {
  column: PartsMasterColumnDescriptor;
  visible: boolean;
  index: number;
  onToggle: (id: string) => void;
  onReorder: (dragIndex: number, hoverIndex: number) => void;
}

function DraggableRow({
  column,
  visible,
  index,
  onToggle,
  onReorder,
}: DraggableRowProps) {
  // Track hover-highlight locally so the className stays in sync with the
  // drag-leave / drop events without forcing a parent re-render.
  const [hover, setHover] = useState(false);

  const handleDragStart = (e: DragEvent<HTMLDivElement>) => {
    e.dataTransfer.setData('text/plain', index.toString());
    e.currentTarget.classList.add('opacity-50');
  };

  const handleDragEnd = (e: DragEvent<HTMLDivElement>) => {
    e.currentTarget.classList.remove('opacity-50');
    setHover(false);
  };

  const handleDragOver = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setHover(true);
  };

  const handleDragLeave = () => {
    setHover(false);
  };

  const handleDrop = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setHover(false);
    const raw = e.dataTransfer.getData('text/plain');
    const dragIndex = parseInt(raw, 10);
    if (!Number.isNaN(dragIndex) && dragIndex !== index) {
      onReorder(dragIndex, index);
    }
  };

  return (
    <div
      draggable
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      className={`flex items-center gap-2 px-2 py-1.5 rounded cursor-move group ${
        hover ? 'bg-primary/10' : 'hover:bg-muted/50'
      }`}
    >
      <GripVertical className="h-4 w-4 text-muted-foreground/50 group-hover:text-muted-foreground flex-shrink-0" />
      <input
        type="checkbox"
        checked={visible}
        onChange={() => onToggle(column.id)}
        onClick={(e) => e.stopPropagation()}
        className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary cursor-pointer"
      />
      <span className="text-sm flex-1">{column.label}</span>
      {visible ? (
        <Eye className="h-3 w-3 text-muted-foreground" />
      ) : (
        <EyeOff className="h-3 w-3 text-muted-foreground" />
      )}
    </div>
  );
}
